import { addMinutes, formatISO } from 'date-fns';
import { z } from 'zod';
import { Messages } from '../../constants';
import { EventBodyContentTypeEnum } from './EventBodyContentTypeEnum';
import { calendarEventAttendeeSchema } from './ICalendarEventAttendee';
import { ICalendarEventForm } from './ICalendarEventForm';

export const calendarEventSchema = z.object({
  id: z.string().optional(),
  subject: z.string().min(1, { message: Messages.Required }),
  start: z.object({
    dateTime: z
      .string()
      .min(1, { message: Messages.Required })
      .refine(date => !isNaN(Date.parse(date)), {
        message: Messages.InvalidDateFormat,
      }),
    timeZone: z.string(),
  }),
  end: z.object({
    dateTime: z
      .string()
      .min(1, { message: Messages.Required })
      .refine(date => !isNaN(Date.parse(date)), {
        message: Messages.InvalidDateFormat,
      }),
    timeZone: z.string(),
  }),
  isAllDay: z.boolean(),
  location: z
    .object({
      displayName: z.string().optional(),
      address: z.record(z.string()).optional(), // Address as an object (key-value pairs)
    })
    .optional(),
  body: z.object({
    contentType: z.nativeEnum(EventBodyContentTypeEnum),
    content: z.string().optional(),
  }),
  attendees: z.array(calendarEventAttendeeSchema).optional(),
});
export type ICalendarEvent = z.infer<typeof calendarEventSchema>;

export const initEvent = (selectedDate: Date): ICalendarEventForm => {
  // Wenn der User auf eine Zelle im Wochen-/Tages-Raster geklickt hat,
  // bringt ``selectedDate`` bereits eine konkrete Uhrzeit mit — diese
  // sollen wir uebernehmen, statt sie durch die aktuelle Systemzeit zu
  // ueberschreiben. Bei Monats-Klicks ist die Uhrzeit 00:00:00 — in dem
  // Fall fallen wir auf die aktuelle Zeit (auf 30 Minuten gerundet) zurueck.
  const hasExplicitTime =
    selectedDate.getHours() !== 0 ||
    selectedDate.getMinutes() !== 0 ||
    selectedDate.getSeconds() !== 0;

  let startHours: number;
  let startMinutes: number;
  if (hasExplicitTime) {
    startHours = selectedDate.getHours();
    startMinutes = selectedDate.getMinutes();
  } else {
    const currentTime = new Date();
    const currentMinutes = currentTime.getMinutes();
    startMinutes = currentMinutes < 15 ? 0 : currentMinutes < 45 ? 30 : 0;
    startHours =
      currentMinutes >= 45
        ? currentTime.getHours() + 1
        : currentTime.getHours();
  }

  const now = new Date(
    selectedDate.getFullYear(),
    selectedDate.getMonth(),
    selectedDate.getDate(),
    startHours,
    startMinutes,
    0
  );

  const defaultStartDateTime = formatISO(now, {
    representation: 'complete',
  });

  // Neue Termine standardmaessig 60 Min lang, wenn der User aus einer
  // Zelle heraus startet — sonst die bisherigen 30 Minuten.
  const defaultDurationMin = hasExplicitTime ? 60 : 30;
  const endTime = addMinutes(now, defaultDurationMin);

  const defaultEndDateTime = formatISO(endTime, {
    representation: 'complete',
  });

  return {
    subject: '',
    start: {
      dateTime: defaultStartDateTime,
      timeZone: 'UTC',
    },
    end: {
      dateTime: defaultEndDateTime,
      timeZone: 'UTC',
    },
    body: { contentType: EventBodyContentTypeEnum.Text },
    isAllDay: false,
    // isCancelled: false,
    // responseRequested: true,
    // importance: 'normal',
    // showAs: 'busy',
    requiredAttendees: [],
    optionalAttendees: [],
    // webLink: '',
    // createdDateTime: new Date().toISOString(),
    // lastModifiedDateTime: new Date().toISOString(),
    // organizer: { emailAddress: { name: LastName, address: Email } },
    // calendar: { name: 'Calendar' },
  };
};
